/* =============================================================== <keis-manual>
   The interactive manual (sections/manual-guide.liquid).

   Choosing an item (a tab, or its part of the drawing) shows how to use it
   and lights its layers: they take the highlight tones, everything else
   stays in muted tones, and the view moves in on them. An item with no layer names
   shows the whole drawing.

   A layer belongs to an item when its name contains one of the item's words
   (data-parts, comma separated). The name is what the drawing program saved
   (inkscape:label, data-name or id), with Illustrator's _x5F_ escapes and
   _1_ numbering undone, compared without case, spaces, dashes or
   underscores: "Left_Glove_1_" contains "glove". The built-in drawing is
   named the same way, with data-name.

   /pages/manuals#gloves opens the item whose link name is "gloves" and
   brings the manual into view, on arrival and whenever the address's #
   changes (the Manuals library's buttons link like this).

   An uploaded SVG is read as text and put into the page as live SVG,
   cleaned of scripts and with its styles scoped, so its layers can be lit.
   ========================================================================= */
(function () {
  'use strict';
  if (customElements.get('keis-manual')) return;

  var SHAPES = 'g, path, rect, circle, ellipse, line, polyline, polygon, use, text, image';
  var FRAME_MS = 700;

  function layerName(el) {
    var raw = el.getAttribute('inkscape:label') || el.getAttribute('data-name') || el.id || '';
    return raw.replace(/_x([0-9a-f]{2})_/gi, function (m, h) { return String.fromCharCode(parseInt(h, 16)); })
      .replace(/_\d+_$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  }
  var squash = function (s) { return String(s || '').toLowerCase().replace(/[\s_\-.]+/g, ''); };

  function clean(svg) {
    Array.prototype.forEach.call(svg.querySelectorAll('script, foreignObject'), function (n) { n.remove(); });
    Array.prototype.forEach.call(svg.querySelectorAll('image'), function (n) {
      var href = n.getAttribute('href') || n.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || '';
      if (!/^\s*(data:|https?:|\/\/)/i.test(href)) n.remove();
    });
    Array.prototype.forEach.call(svg.querySelectorAll('*'), function (n) {
      Array.prototype.slice.call(n.attributes).forEach(function (a) {
        var v = String(a.value || '');
        if (/^on/i.test(a.name) || (/href$/i.test(a.name) && /^\s*javascript:/i.test(v))) n.removeAttribute(a.name);
      });
    });
  }

  /* Every export names its styles .st0, .st1…; scoped per drawing so two on
     one page cannot restyle each other. */
  var uid = 0;
  function scopeStyles(svg) {
    var styles = svg.querySelectorAll('style');
    if (!styles.length) return;
    var prefix = 'kmg' + (++uid) + '-';
    Array.prototype.forEach.call(styles, function (st) {
      st.textContent = st.textContent.replace(/\.(-?[_a-zA-Z][\w-]*)/g, function (m, name) {
        return /^\d/.test(name) ? m : '.' + prefix + name;
      });
    });
    Array.prototype.forEach.call(svg.querySelectorAll('[class]'), function (el) {
      el.setAttribute('class', el.getAttribute('class').split(/\s+/).filter(Boolean).map(function (c) { return prefix + c; }).join(' '));
    });
  }

  /* An uploaded drawing keeps its shapes but not its colours: it is redrawn
     in the page's own tones, so it reads on the light and the carbon bands
     alike. Garments (by layer name) take --mg-g, their outlines and stitching
     blended towards --mg-l by how dark they were drawn, and anything coloured
     inside a garment takes --mg-l too; the rest of the kit (leads, battery,
     controller, panels) takes --mg-t. The three are set in
     keis-guides.css per band, and a lit layer swaps in its own set, so the
     chosen item changes colour as one. */
  var GARMENT = /jacket|vest|bodywarmer|glove|trouser|sock|cushion/;
  function rgb(v) {
    var m = String(v || '').match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    return { r: p[0] / 255, g: p[1] / 255, b: p[2] / 255, a: p.length > 3 ? p[3] : 1 };
  }
  function neutral(c) { return Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b) < 0.09; }
  function lightness(c) { return (Math.max(c.r, c.g, c.b) + Math.min(c.r, c.g, c.b)) / 2; }
  function inGarment(el, svg) {
    for (var n = el; n && n !== svg; n = n.parentElement) {
      if (GARMENT.test(squash(layerName(n)))) return true;
    }
    return false;
  }
  function recolour(svg) {
    var jobs = [], top = 0;
    Array.prototype.forEach.call(svg.querySelectorAll('path, rect, circle, ellipse, line, polyline, polygon, text'), function (el) {
      if (el.closest('defs, clipPath, mask, symbol')) return;
      var cs = window.getComputedStyle(el), garment = inGarment(el, svg);
      ['fill', 'stroke'].forEach(function (prop) {
        var c = rgb(cs[prop]);
        if (!c || c.a === 0) return;
        if (garment && neutral(c)) top = Math.max(top, lightness(c));
        jobs.push([el, prop, c, garment]);
      });
    });
    top = top || 1;
    jobs.forEach(function (j) {
      var el = j[0], prop = j[1], c = j[2], L = lightness(c), v;
      if (j[3] && neutral(c)) v = 'color-mix(in srgb, var(--mg-g) ' + Math.round(Math.min(1, L / top) * 100) + '%, var(--mg-l))';
      /* Kit drawn in black or grey (batteries, controllers) is filled in the
         kit tone, so it lights like everything else; its dark outlines stay
         outlines. */
      else if (neutral(c)) v = prop === 'stroke' && L < 0.5 ? 'var(--mg-l)' : 'var(--mg-t)';
      /* Leads and connectors drawn inside a garment are part of the garment:
         they take its outline tone, so a lit garment shows only itself, not
         the kit's colours. */
      else if (j[3]) v = 'var(--mg-l)';
      else v = 'var(--mg-t)';
      if (c.a < 1) v = 'color-mix(in srgb, ' + v + ' ' + Math.round(c.a * 100) + '%, transparent)';
      el.style[prop] = v;
    });
  }

  function fit(b, full) {
    var aspect = full.w / full.h;
    var pad = Math.max(b.x1 - b.x0, b.y1 - b.y0) * 0.25 + full.w * 0.05;
    var w = b.x1 - b.x0 + pad * 2, h = b.y1 - b.y0 + pad * 2;
    w = Math.max(w, full.w * 0.5); h = Math.max(h, full.h * 0.5);
    if (w / h < aspect) w = h * aspect; else h = w / aspect;
    if (w >= full.w || h >= full.h) return full;
    var cx = (b.x0 + b.x1) / 2, cy = (b.y0 + b.y1) / 2;
    return {
      x: Math.min(Math.max(cx - w / 2, full.x), full.x + full.w - w),
      y: Math.min(Math.max(cy - h / 2, full.y), full.y + full.h - h),
      w: w, h: h
    };
  }

  class Manual extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-mg-item]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-mg-panel]'));
      if (!this.tabs.length) return;
      this.words = this.tabs.map(function (t) {
        return (t.getAttribute('data-parts') || '').split(',').map(squash).filter(Boolean);
      });

      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-mg-item]');
        if (tab) { self.select(Number(tab.getAttribute('data-mg-item')), true); return; }
        /* Tapping the drawing picks the item that part belongs to. */
        var svg = self.svg();
        if (svg && svg.contains(e.target)) {
          var i = self.itemAt(e.target);
          if (i !== -1) self.select(i, true);
        }
      });
      this.addEventListener('keydown', function (e) {
        if (!e.target.closest('[data-mg-item]')) return;
        var d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        self.select(self.index + d, true);
        self.tabs[self.index].focus();
      });
      document.addEventListener('shopify:block:select', function (e) {
        var i = self.tabs.indexOf(e.target);
        if (i !== -1) self.select(i, false);
      });
      window.addEventListener('hashchange', function () { self.fromHash(true); });

      this.classList.add('is-ready');
      if (!this.fromHash(true)) this.select(0, false);
      this.load();
    }

    disconnectedCallback() { if (this.raf) cancelAnimationFrame(this.raf); }

    fromHash(scroll) {
      var want = decodeURIComponent(window.location.hash.slice(1)).toLowerCase();
      if (!want) return false;
      var i = this.tabs.findIndex(function (t) { return (t.getAttribute('data-link') || '').toLowerCase() === want; });
      if (i === -1) return false;
      this.select(i, false);
      if (scroll) {
        var self = this;
        requestAnimationFrame(function () { self.scrollIntoView({ block: 'start', behavior: 'smooth' }); });
      }
      return true;
    }

    select(i, byVisitor) {
      var n = this.tabs.length;
      i = ((i % n) + n) % n;
      this.index = i;
      this.tabs.forEach(function (t, k) { t.setAttribute('aria-selected', k === i ? 'true' : 'false'); t.tabIndex = k === i ? 0 : -1; });
      this.panels.forEach(function (p, k) { p.classList.toggle('is-active', k === i); });
      this.light(this.words[i]);
      if (byVisitor && window.history && window.history.replaceState) {
        var link = this.tabs[i].getAttribute('data-link');
        if (link) window.history.replaceState(null, '', '#' + link);
      }
    }

    svg() { return this.querySelector('.keis-mg__figure svg'); }

    matches(el, words) {
      var name = squash(layerName(el) + ' ' + (el.getAttribute('data-zone') || ''));
      return !!name && words.some(function (w) { return name.indexOf(w) !== -1; });
    }

    /* The item a tapped shape belongs to: the nearest layer, going out from
       the shape, whose name one of the items claims. */
    itemAt(target) {
      var svg = this.svg();
      for (var el = target; el && el !== svg; el = el.parentElement) {
        for (var i = 0; i < this.words.length; i++) {
          if (this.words[i].length && this.matches(el, this.words[i])) return i;
        }
      }
      return -1;
    }

    /* Mark every part an item claims, so it can show it can be tapped. */
    markHits() {
      var svg = this.svg(), self = this;
      if (!svg) return;
      var all = [].concat.apply([], this.words);
      if (!all.length) return;
      Array.prototype.forEach.call(svg.querySelectorAll(SHAPES), function (el) {
        if (self.matches(el, all)) el.classList.add('is-hit');
      });
    }

    /* Light an item's layers, fade the rest, and frame them. */
    light(words) {
      var svg = this.svg(), self = this;
      if (!svg) return;
      Array.prototype.forEach.call(svg.querySelectorAll('.is-lit, .is-dim'), function (el) { el.classList.remove('is-lit', 'is-dim'); });
      svg.classList.toggle('has-focus', words.length > 0);
      if (!words.length) { this.frame(null); return; }

      var lit = [];
      Array.prototype.forEach.call(svg.querySelectorAll(SHAPES), function (el) {
        if (el.closest('defs, clipPath, mask, symbol')) return;
        if (self.matches(el, words) && !lit.some(function (p) { return p.contains(el); })) lit.push(el);
      });
      lit.forEach(function (el) { el.classList.add('is-lit'); });

      /* Fade whole branches with nothing lit in them, from the top down, so
         faded layers inside faded layers do not compound. */
      var fade = function (parent) {
        Array.prototype.forEach.call(parent.children, function (el) {
          if (!el.matches(SHAPES) || el.classList.contains('is-lit')) return;
          if (el.querySelector('.is-lit')) fade(el);
          else el.classList.add('is-dim');
        });
      };
      fade(svg);
      this.frame(lit);
    }

    frame(lit) {
      var svg = this.svg();
      if (!svg || !svg.viewBox || !svg.getScreenCTM) return;
      if (!this.full || this.full.svg !== svg) {
        var vb = svg.viewBox.baseVal;
        if (!vb || !vb.width) return;
        this.full = { svg: svg, x: vb.x, y: vb.y, w: vb.width, h: vb.height };
      }
      var target = this.full, ctm = svg.getScreenCTM();
      if (lit && lit.length && ctm) {
        var inv = ctm.inverse(), box = null;
        lit.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (!r.width && !r.height) return;
          [[r.left, r.top], [r.right, r.bottom]].forEach(function (p) {
            var pt = svg.createSVGPoint(); pt.x = p[0]; pt.y = p[1];
            pt = pt.matrixTransform(inv);
            if (!box) box = { x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y };
            box.x0 = Math.min(box.x0, pt.x); box.y0 = Math.min(box.y0, pt.y);
            box.x1 = Math.max(box.x1, pt.x); box.y1 = Math.max(box.y1, pt.y);
          });
        });
        if (box) target = fit(box, this.full);
      }
      this.moveTo(svg, target);
    }

    moveTo(svg, to) {
      var set = function (b) { svg.setAttribute('viewBox', [b.x, b.y, b.w, b.h].map(function (n) { return +n.toFixed(2); }).join(' ')); };
      if (this.raf) cancelAnimationFrame(this.raf);
      var vb = svg.viewBox.baseVal;
      var from = { x: vb.x, y: vb.y, w: vb.width, h: vb.height };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { set(to); return; }
      var self = this, t0 = null;
      var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
      var tick = function (now) {
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / FRAME_MS), k = ease(t);
        set({ x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k, w: from.w + (to.w - from.w) * k, h: from.h + (to.h - from.h) * k });
        self.raf = t < 1 ? requestAnimationFrame(tick) : null;
      };
      this.raf = requestAnimationFrame(tick);
    }

    /* An uploaded drawing: shown as a picture until it has been read, then
       swapped for the live copy. If it cannot be read, the picture stays. */
    load() {
      var self = this;
      var box = this.querySelector('[data-mg-src]');
      if (!box) { this.markHits(); this.select(this.index || 0, false); return; }
      if (!window.fetch || !window.DOMParser) return;
      fetch(box.getAttribute('data-mg-src'))
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          var svg = doc.documentElement;
          if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) return;
          clean(svg);
          scopeStyles(svg);
          if (!svg.getAttribute('viewBox')) {
            var w = parseFloat(svg.getAttribute('width')), h = parseFloat(svg.getAttribute('height'));
            if (w && h) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
          }
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          svg.setAttribute('focusable', 'false');
          box.replaceChildren(document.importNode(svg, true));
          self.classList.add('keis-mg--custom');
          recolour(self.svg());
          self.full = null;
          self.markHits();
          self.select(self.index || 0, false);
        })
        .catch(function () {});
    }
  }

  customElements.define('keis-manual', Manual);
})();
